import { useMemo, useState } from 'react'
import { dateFormat, numberFormat } from 'shared/util'

import {
	Stack,
	ListItem,
	ListItemButton,
	Typography,
	ListItemProps,
	Button,
} from '@mui/material'
import { ChevronRight as OpenIcon } from '@mui/icons-material'

import { LvProjectResult, useCountLvHaisin, useListLvLog } from 'interface/carenet/lv'

type LvProjectRecordProps = {
	project: LvProjectResult
	openLvProject: (projectCode: string, isOpenGrid: boolean) => void
} & ListItemProps
export const LvProjectRecord = ({
	project,
	openLvProject,
	...others
}: LvProjectRecordProps) => {
	const [open, setOpen] = useState<boolean>(false)
	return (
		<ListItem {...others} disablePadding>
			<ListItemButton onClick={() => setOpen(!open)} disableGutters disableRipple sx={{ paddingX: 1 }}>
				<Stack direction="row" boxSizing="border-box" width="100%" alignItems="flex-start">
					<OpenIcon color="action" style={{
						transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
						transition: 'transform 0.2s ease-in-out'
					}} />
					<Stack direction="column" flexGrow={1} overflow="hidden" paddingX={1}>
						<Stack direction="row" spacing={1} alignItems="flex-start">
							<Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
								{dateFormat(project.broadcastStartDate, { wareki: false })}
							</Typography>
							<div
								dangerouslySetInnerHTML={{
									__html: project.projectName ?? 'empty'
								}}
								style={open ? {
									flexGrow: 1
								} : {
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									flexGrow: 1
								}}
							/>
							<Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
								{project.productName}
							</Typography>
						</Stack>
						
						{open && (
							<LvProjectDetail
								project={project}
								openLvProject={openLvProject}
							/>
						)}
					</Stack>
				</Stack>
			</ListItemButton>
		</ListItem>
	)
}

type LvProjectDetailProps = {
	project: LvProjectResult
	openLvProject: (projectCode: string, isOpenGrid: boolean) => void
}
const LvProjectDetail = ({
	project,
	openLvProject,
}: LvProjectDetailProps) => {
	const lvHaisinCountState = useCountLvHaisin(project.projectCode)
	const lvLogState = useListLvLog(project.projectCode)
	const summary = useMemo(
		() => {
			if (!lvHaisinCountState.data || !lvLogState.data) return null
			const haisinCount = lvHaisinCountState.data.count
			const viewCount = lvLogState.data.list.length
			const rate = haisinCount > 0 ? (Math.floor((viewCount * 100 * 100) / haisinCount) / 100) : null
			return {
				haisinCount,
				viewCount,
				rate
			}
		},
		[lvLogState.data, lvHaisinCountState.data]
	)
	return (
		<Stack direction="column" width="100%" spacing={1} paddingY={0.5}>
			<Stack direction="column">
				{project.reservationDate && (
					<Stack direction="row" spacing={0.5} alignItems="baseline">
						<Typography variant="caption" color="text.secondary">予約：</Typography>
						<Typography variant="body2" color="text.primary">{dateFormat(project.reservationDate, { wareki: true })}</Typography>
						<Typography variant="caption" color="text.secondary">～</Typography>
					</Stack>
				)}
				{project.broadcastStartDate && (
					<Stack direction="row" spacing={0.5} alignItems="baseline">
						<Typography variant="caption" color="text.secondary">配信：</Typography>
						<Typography variant="body2" color="text.primary">{dateFormat(project.broadcastStartDate, { wareki: true })}</Typography>
						{project.broadcastEndDate && project.broadcastEndDate !== project.broadcastStartDate && (
							<>
								<Typography variant="caption" color="text.secondary">～</Typography>
								<Typography variant="body2" color="text.primary">{dateFormat(project.broadcastEndDate, { wareki: true })}</Typography>
							</>
						)}
					</Stack>
				)}
				{project.rebroadcastStartDate && (
					<Stack direction="row" spacing={0.5} alignItems="baseline">
						<Typography variant="caption" color="text.secondary">再配信：</Typography>
						<Typography variant="body2" color="text.primary">{dateFormat(project.rebroadcastStartDate, { wareki: true })}</Typography>
						{project.rebroadcastEndDate && project.rebroadcastEndDate !== project.rebroadcastStartDate && (
							<>
								<Typography variant="caption" color="text.secondary">～</Typography>
								<Typography variant="body2" color="text.primary">{dateFormat(project.rebroadcastEndDate, { wareki: true })}</Typography>
							</>
						)}
					</Stack>
				)}
				{summary && (
					<Stack direction="row" spacing={1} alignItems="center">
						<Stack direction="row" alignItems="baseline">
							<Typography variant="caption" color="text.secondary">配信対象：</Typography>
							<Typography variant="body2" color="text.primary">{numberFormat(summary.haisinCount)}</Typography>
							<Typography variant="caption" color="text.secondary">件</Typography>
						</Stack>
						{summary.viewCount === 0 ? (
							<Typography variant="caption" color="text.secondary">（未配信）</Typography>
						) : (
							<>
								<Stack direction="row" spacing={0.5} alignItems="baseline">
									<Typography variant="caption" color="text.secondary">視聴数：</Typography>
									<Typography variant="body2" color="text.primary">{numberFormat(summary.viewCount)}</Typography>
									<Typography variant="caption" color="text.secondary">件</Typography>
								</Stack>
								{summary.rate !== null && (
									<Stack direction="row" spacing={0.5} alignItems="baseline">
										<Typography variant="caption" color="text.secondary">視聴率：</Typography>
										<Typography variant="body2" color="text.primary">{summary.rate}</Typography>
										<Typography variant="caption" color="text.secondary">%</Typography>
									</Stack>
								)}
							</>
						)}
					</Stack>
				)}
			</Stack>
			<Stack direction="row" spacing={1} marginTop={1} justifyContent="flex-start">
				<Button
					variant="outlined"
					color="primary"
					size="small"
					onClick={(ev) => {
						ev.stopPropagation
						openLvProject(project.projectCode, false)
					}}
				>
					ダッシュボード分析
				</Button>

				<Button
					variant="outlined"
					color="primary"
					size="small"
					onClick={(ev) => {
						ev.stopPropagation()
						openLvProject(project.projectCode, true)
					}}
				>
					シート分析
				</Button>
			</Stack>
		</Stack>
	)
}
